import React from "react";
import { Box, useTheme } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useSearchParams } from "react-router-dom";
import { useStores } from "../../../use-stores";
import StorePreview from "../../components/swagstore/StorePreview";
import {
  SWAGSTORE_STEPS,
  SWAGSTORE_STEP_VALUES,
} from "../../../../../constants/SwagstoreConstants";

const StorePreviewPage: React.FC = observer(() => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const { swagStoreStore } = useStores();
  const storeId = searchParams.get("store_id");
  const themeId = searchParams.get("theme_id");
  const step = searchParams.get("step");

  // Get store background color from branding settings
  const storeBackgroundColor =
    swagStoreStore.settings.branding?.storeBackgroundColor ||
    theme.palette.customColors.white;

  // Load store data if store_id is provided
  React.useEffect(() => {
    if (storeId && storeId.trim() !== "") {
      if (
        !swagStoreStore.storeId ||
        swagStoreStore.storeId !== storeId
      ) {
        swagStoreStore.setStoreId(storeId);
      }
    }
    if (themeId) {
      const parsedThemeId = Number(themeId);
      if (!isNaN(parsedThemeId) && parsedThemeId > 0) {
        // Set theme ID if not already set or different
        if (
          !swagStoreStore.themeId ||
          swagStoreStore.themeId !== parsedThemeId
        ) {
          swagStoreStore.setThemeId(parsedThemeId);
        }
      }
    }
  }, [storeId, themeId, swagStoreStore]);

  // Load store details and theme when storeId and themeId are available
  React.useEffect(() => {
    if (swagStoreStore.storeId && swagStoreStore.themeId) {
      swagStoreStore
        .getStoreDetails()
        .then(() => {
          if (swagStoreStore.settings.storeDetails.customer_id) {
            Promise.all([
              swagStoreStore.getPacks().catch((error) => {
                console.error("Error loading inventory products:", error);
              }),
              swagStoreStore.getProducts().catch((error) => {
                console.error("Error loading catalogue products:", error);
              }),
            ]);
          }
        })
        .catch((error) => {
          console.error("Error loading store details:", error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPreviewWidth = (): string => {
    switch (swagStoreStore.deviceView) {
      case "mobile":
        return "375px";
      case "tablet":
        return "768px";
      case "desktop":
      default:
        return "100%";
    }
  };

  const getLoginPageImageUrl = (): string | undefined => {
    const loginImage = swagStoreStore.settings.loginPage.loginPageImage;
    if (loginImage instanceof File) {
      return URL.createObjectURL(loginImage);
    }
    if (typeof loginImage === "string" && loginImage.trim()) {
      return loginImage;
    }
    return undefined;
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        minHeight: "100vh",
        overflow: "hidden",
        backgroundColor: storeBackgroundColor,
      }}
    >
      <StorePreview
        settings={swagStoreStore.settings}
        selectedItem={
          step && SWAGSTORE_STEP_VALUES.includes(step)
            ? step
            : SWAGSTORE_STEPS.PRODUCT_MAPPING
        }
        deviceView={swagStoreStore.deviceView}
        onDeviceViewChange={swagStoreStore.setDeviceView}
        previewWidth={getPreviewWidth()}
        loginPageImageUrl={getLoginPageImageUrl()}
        hidePreviewWrapper={true}
        isStandalonePreview={true}
      />
    </Box>
  );
});

export default StorePreviewPage;
